import React, {useEffect, useRef, useState} from 'react';
import {Input, Tree} from 'antd';
import {DownOutlined, HomeOutlined} from '@ant-design/icons';
import type {DataNode, TreeProps} from 'antd/es/tree';
import {useQuery} from '@apollo/client';
import {WarehouseType} from 'graphql/types/WarehouseType';
import {SiteType} from 'graphql/types/SiteType';
import { useTranslation } from 'react-i18next';
import { GET_HUB_AND_WAREHOUSES_FOR_SITE_MANAGEMENT } from '../graphql/queries/getHubAndWarehousesForSiteManagement';

const {Search} = Input;

interface WmsSitesTreeProps {
    onSelect?: (key: string) => void;
    hubFilterId?: number | string;
}

const HubTreeTitle = ({hub}: { hub: WarehouseType }) => (
    <div className="flex items-center gap-2 w-full min-w-0">
        <span className="font-medium truncate">{hub.name}</span>
        <span className="text-xs text-gray-500 shrink-0">({hub.sortingCode})</span>
    </div>
);

const WarehouseTreeTitle = ({warehouse}: { warehouse: WarehouseType }) => (
    <div className="flex items-center gap-2 w-full min-w-0">
        <HomeOutlined className="shrink-0"/>
        <span className="truncate">{warehouse.name}</span>
        <span className="text-xs text-gray-500 shrink-0">({warehouse.sortingCode})</span>
    </div>
);

const WmsSitesTree: React.FC<WmsSitesTreeProps> = ({
                                                 onSelect,
                                                 hubFilterId,
                                             }) => {
    const { t } = useTranslation();

    const { data } = useQuery(GET_HUB_AND_WAREHOUSES_FOR_SITE_MANAGEMENT);
    const allWarehouses = data?.warehouses || [];

    const [selectedKey, setSelectedKey] = useState<string>('');
    const [treeData, setTreeData] = useState<DataNode[]>([]);
    const [filteredTreeData, setFilteredTreeData] = useState<DataNode[]>([]);
    const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);
    const [searchValue, setSearchValue] = useState<string>('');

    useEffect(() => {
        if (allWarehouses?.length > 0) {
            const compareByName = (a?: { name?: string; sortingCode?: string; id?: number | string }, b?: { name?: string; sortingCode?: string; id?: number | string }) => {
                const aName = (a?.name || '').trim();
                const bName = (b?.name || '').trim();
                if (aName && bName) {
                    const cmp = aName.localeCompare(bName, undefined, { sensitivity: 'base', numeric: true });
                    if (cmp !== 0) return cmp;
                } else if (aName || bName) {
                    return aName ? -1 : 1;
                }

                const aCode = (a?.sortingCode || '').trim();
                const bCode = (b?.sortingCode || '').trim();
                if (aCode && bCode) {
                    const cmp = aCode.localeCompare(bCode, undefined, { sensitivity: 'base', numeric: true });
                    if (cmp !== 0) return cmp;
                } else if (aCode || bCode) {
                    return aCode ? -1 : 1;
                }

                return (Number(a?.id) || 0) - (Number(b?.id) || 0);
            };

            const warehouses = allWarehouses.map((w: any) => ({
                ...w,
                id: Number(w.id),
                parentSiteId: Number(w.parentSiteId)
            }));

            let filteredWarehouses = warehouses;
            if (hubFilterId) {
                const hubIdNum = Number(hubFilterId);
                filteredWarehouses = warehouses.filter((w: any) => {
                    if (w.siteType === SiteType.HUB && w.id === hubIdNum) {
                        return true;
                    }
                    if (w.siteType === SiteType.WAREHOUSE && w.parentSiteId === hubIdNum) {
                        return true;
                    }
                    return false;
                });
            }

            const hubMap = new Map<number, { hub: WarehouseType; warehouses: WarehouseType[] }>();

            filteredWarehouses.forEach((warehouse: any) => {
                if (warehouse.siteType === SiteType.HUB) {
                    hubMap.set(warehouse.id || 0, {hub: warehouse, warehouses: []});
                }
            });

            filteredWarehouses.forEach((warehouse: any) => {
                if (warehouse.siteType === SiteType.WAREHOUSE) {
                    const hub = hubMap.get(warehouse.parentSiteId || 0);
                    if (hub) {
                        hub.warehouses.push(warehouse);
                    }
                }
            });

            const sortedHubs = Array.from(hubMap.values()).sort((a, b) => compareByName(a.hub, b.hub));
            sortedHubs.forEach(group => {
                group.warehouses.sort((a, b) => compareByName(a, b));
            });

            const treeNodes: DataNode[] = sortedHubs.map(hub => ({
                key: hub.hub.id || '',
                title: <HubTreeTitle hub={hub.hub} />,
                isLeaf: false,
                searchData: {
                    name: hub.hub.name || '',
                    sortingCode: hub.hub.sortingCode || ''
                },
                children: hub.warehouses.map(warehouse => ({
                    key: warehouse.id || '',
                    isLeaf: true,
                    title: <WarehouseTreeTitle warehouse={warehouse}/>,
                    searchData: {
                        name: warehouse.name || '',
                        sortingCode: warehouse.sortingCode || ''
                    }
                }))
            }));

            setTreeData(treeNodes);
        }
    }, [allWarehouses, hubFilterId]);

    useEffect(() => {
        const filtered = filterTreeData(treeData, searchValue);
        setFilteredTreeData(filtered);
    }, [treeData, searchValue]);

    const lastAppliedHubFilterIdRef = useRef<number | string | undefined>(undefined);
    useEffect(() => {
        if (hubFilterId == null) return;
        if (lastAppliedHubFilterIdRef.current === hubFilterId) return;
        lastAppliedHubFilterIdRef.current = hubFilterId;
        const hubKey = Number(hubFilterId) as unknown as string;
        setSelectedKey(hubKey);
        onSelect?.(String(hubFilterId));
    }, [hubFilterId, onSelect]);

    const handleSelect: TreeProps['onSelect'] = (selectedKeys) => {
        if (selectedKeys.length > 0) {
            const key = selectedKeys[0] as string;
            setSelectedKey(key);
            onSelect?.(key);
        }
    };

    const handleSearch = (value: string) => {
        setSearchValue(value);
        if (value) {
            const keysToExpand: React.Key[] = [];
            treeData.forEach(node => {
                if (node.children?.length) {
                    keysToExpand.push(node.key);
                }
            });
            setExpandedKeys(keysToExpand);
        }
    };

    const filterTreeData = (data: DataNode[], searchText: string): DataNode[] => {
        if (!searchText) return data;

        const searchLower = searchText.toLowerCase();

        return data.map(node => {
            const hubSearchData = (node as any).searchData;
            const hubMatches = hubSearchData && (
                hubSearchData.name.toLowerCase().includes(searchLower) ||
                hubSearchData.sortingCode.toLowerCase().includes(searchLower)
            );

            const filteredChildren = node.children?.filter(child => {
                const warehouseSearchData = (child as any).searchData;
                return warehouseSearchData && (
                    warehouseSearchData.name.toLowerCase().includes(searchLower) ||
                    warehouseSearchData.sortingCode.toLowerCase().includes(searchLower)
                );
            }) || [];

            if (hubMatches || filteredChildren.length > 0) {
                return {
                    ...node,
                    children: hubMatches ? node.children : filteredChildren
                };
            }

            return null;
        }).filter(Boolean) as DataNode[];
    };

    return (
        <div className="border-r border-gray-200 p-4 flex flex-col gap-2 w-[360px] shrink-0 h-full overflow-y-auto">
            <Search
                placeholder={t('central.sites.placeholders.searchHubWarehouse') as string}
                className="mb-4"
                allowClear
                value={searchValue}
                onChange={(e) => handleSearch(e.target.value)}
                onSearch={handleSearch}
            />

            <Tree
                showIcon
                expandedKeys={expandedKeys}
                onExpand={setExpandedKeys}
                selectedKeys={selectedKey ? [selectedKey] : []}
                switcherIcon={<DownOutlined/>}
                onSelect={handleSelect}
                treeData={searchValue ? filteredTreeData : treeData}
                className="w-full [&_.ant-tree-indent-unit]:w-4"
            />
        </div>
    );
};

export default WmsSitesTree;
